/**
 * Bathroom murals — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_murals`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bathroomMuralsPack: ToilePack = {
    slug: 'bathroom-murals',
    category: 'wall-art',
    name: 'Bathroom murals',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'underwater', label: 'Underwater', subject: 'a wall painted with seaweed, coral and swimming fish in blues and greens, behind a white clawfoot tub'},
        {key: 'tropical', label: 'Tropical leaves', subject: 'a wall painted with large tropical leaves and a pink flamingo, around a round mirror over a small sink'},
        {key: 'clouds', label: 'Clouds', subject: 'soft white clouds painted on a pale blue wall and ceiling in a small bathroom with a brass towel rail'},
    ],
    styles: [
        {key: 'watercolor', label: 'Watercolor', style: 'hand-painted in soft watercolor washes on moisture-proof matte paint, the edges gently bled', palette: 'the room in white tile, brass and a cotton towel', framing: 'a wide eye-level view of the whole room'},
        {key: 'bold', label: 'Bold flat', style: 'painted in bold saturated flat shapes with crisp clean edges', palette: 'the room in terrazzo and pale wood', framing: 'a straight-on view of the painted wall'},
        {key: 'trompe', label: "Trompe l'oeil", style: "painted in a soft realistic trompe-l'oeil manner with depth and gentle shading", palette: 'the room in white marble and chrome', framing: 'a three-quarter view from the doorway'},
    ],
    directions: ['starry night', 'botanical', 'powder room'],
    directionHint: 'try: starry night',
    seo: {
        title: 'Bathroom Mural: Underwater, Tropical, Clouds',
        metaDescription: "Nine bathroom mural pictures: an underwater scene, tropical leaves and painted clouds, in watercolor, bold flat and trompe-l'oeil styles.",
        h1: 'Bathroom mural: underwater, tropical leaves or clouds',
        tagline: 'One painted wall in a small bathroom, three scenes, three finishes each.',
        intro: "A bathroom mural is one painted wall in a small room, a place to escape for ten minutes. These nine pictures show an underwater scene of seaweed and fish behind a clawfoot tub, a tropical leaf mural around a round mirror, and soft clouds painted on a pale blue wall and ceiling. Each scene is shown in watercolor washes, bold flat shapes and trompe-l'oeil shading. Type a different subject in the direction field and the same finishes can carry it.",
        useCases: [
            'a wall behind a clawfoot tub',
            'a painted wall around a mirror',
            'a ceiling and walls in soft clouds',
            'a small bathroom refresh',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: "Three bathroom scenes: an underwater wall, tropical leaves and painted clouds. Each is shown in watercolor, bold flat color and trompe-l'oeil."},
            {q: 'Does a mural like this work in a small bathroom?', a: 'Yes. The pictures show small bathrooms with one painted wall, and a tub, a mirror or the ceiling as the focus.'},
            {q: 'Can I get a different subject?', a: 'Yes. Type the subject in the direction field and the same three finishes can carry it. The nine pictures show only the subjects on this page.'},
            {q: 'What is the difference between the finishes?', a: "Watercolor has soft washes with bled edges, bold flat has saturated shapes with crisp edges, and trompe-l'oeil shades each form so it looks real."},
        ],
    },
}
